import { Check, ChevronDown, Pencil, Plus, Search, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn, fuzzyIncludes } from '@/lib/utils'

import type { ComboboxOption } from './combobox'

interface CatalogMultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  onSearch?: (query: string) => void
  options: ComboboxOption[]
  placeholder?: string
  loading?: boolean
  emptyText?: string
  className?: string
  /** Admin: create a new catalog item from the typed search text. */
  onCreate?: (name: string) => void
  /** Admin: edit (rename) an existing catalog item. */
  onEdit?: (option: ComboboxOption) => void
}

/**
 * Multi-select over a global catalog (equipment, services, …). Like
 * MultiCombobox but with optional admin affordances: create a new item from
 * the search text, and rename an existing item via a per-option pencil.
 */
export function CatalogMultiSelect({
  value,
  onChange,
  onSearch,
  options,
  placeholder,
  loading,
  emptyText = '—',
  className,
  onCreate,
  onEdit
}: CatalogMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [dropUp, setDropUp] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const ref = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  const toggleOpen = () =>
    setOpen((prev) => {
      const next = !prev
      if (next && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        // Flip up when there isn't room below and there's more room above.
        setDropUp(spaceBelow < 320 && rect.top > spaceBelow)
      }
      return next
    })

  React.useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const labelFor = (val: string) =>
    options.find((o) => o.value === val)?.label ?? val

  const toggle = (val: string) =>
    onChange(
      value.includes(val) ? value.filter((v) => v !== val) : [...value, val]
    )

  const filtered = onSearch
    ? options
    : options.filter((o) => fuzzyIncludes(o.label, query))

  const trimmed = query.trim()
  const showCreate =
    !!onCreate &&
    trimmed.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === trimmed.toLowerCase())

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-2xl border border-ink/[0.16] bg-white px-3 py-1.5 text-left text-sm transition-colors hover:border-ink/30 focus:border-primary focus:outline-none"
      >
        {value.length === 0 ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : (
          value.map((val) => (
            <span
              key={val}
              className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] py-0.5 pl-2.5 pr-1 text-xs text-ink"
            >
              <span className="max-w-[14rem] truncate">{labelFor(val)}</span>
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation()
                  toggle(val)
                }}
                className="flex h-4 w-4 items-center justify-center rounded-full text-ink/50 hover:text-ink"
              >
                <X className="h-3 w-3" />
              </span>
            </span>
          ))
        )}
        <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-[1200] w-full overflow-hidden rounded-2xl border border-ink/[0.08] bg-white shadow-[0_16px_40px_rgba(4,33,37,0.16)]',
            dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
          )}
        >
          <div className="relative border-b border-ink/[0.06] p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              placeholder={placeholder}
              onChange={(e) => {
                setQuery(e.target.value)
                onSearch?.(e.target.value)
              }}
              className="h-9 border-0 pl-9 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="max-h-60 overflow-y-auto p-1">
            {loading ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">…</div>
            ) : filtered.length === 0 && !showCreate ? (
              <div className="px-3 py-3 text-sm text-muted-foreground">
                {emptyText}
              </div>
            ) : (
              filtered.map((o) => {
                const selected = value.includes(o.value)
                return (
                  <div
                    key={o.value}
                    className={cn(
                      'flex items-center gap-1 rounded-xl pr-1 transition-colors hover:bg-ink/[0.04]',
                      selected && 'text-primary'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(o.value)}
                      className={cn(
                        'flex flex-1 items-center justify-between gap-2 border-0 bg-transparent px-3 py-2 text-left text-sm',
                        selected ? 'font-semibold text-primary' : 'text-ink'
                      )}
                    >
                      <span className="truncate">{o.label}</span>
                      {selected && <Check className="h-4 w-4 shrink-0" />}
                    </button>
                    {onEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="edit"
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpen(false)
                          onEdit(o)
                        }}
                        className="h-7 w-7 rounded-full bg-transparent text-ink/40 hover:bg-ink/[0.06] hover:text-ink"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )
              })
            )}

            {showCreate && (
              <button
                type="button"
                onClick={() => {
                  onCreate?.(trimmed)
                  setQuery('')
                  onSearch?.('')
                }}
                className="mt-1 flex w-full items-center gap-2 rounded-xl border-0 bg-primary/5 px-3 py-2 text-left text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
              >
                <Plus className="h-4 w-4 shrink-0" />
                <span className="truncate">{`"${trimmed}"`}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
